import { css } from 'styled-components'

const rem = {
  baseMobile: 15,
  base: 18,
  get: (value:number, factor = null) => `${value / (factor ?? rem.baseMobile)}rem`
}


// export const vwFont = (size: number, mobile: boolean = true) => rem.get(size, rem[mobile ? 'baseMobile' : 'base'])
// This version memoize outputs of vwFont() so it doesn’t recompute simple divisions on every render.
export const vwFont = (() => {
  const cache = new Map<string, string>()
  const MAX_CACHE_SIZE = 500 // arbitrary safe limit
  return (value: number, mobile = true) => {
    const key = `${value}-${mobile}`
    if (cache.has(key)) {
      return cache.get(key)!
    }
    const result = `${value / (mobile ? rem.baseMobile : rem.base)}rem`
    if (cache.size >= MAX_CACHE_SIZE) {
      cache.clear()
    }
    cache.set(key, result)
    return result
  }
})()


// NEW FONT
const getFontFamilyVarByKey = {
  graphik: '--font-graphik',
  basier: '--font-basier-circle',
  platform: '--font-platform'
}

type FontSizeKeyword =
  | 'inherit' | 'initial' | 'unset'
  | 'xx-small' | 'x-small' | 'small' | 'medium' | 'large'
  | 'x-large' | 'xx-large' | 'xxx-large'
  | 'smaller' | 'larger'
type TypeFontSize = number | FontSizeKeyword | string
type TypeLineHeight = number | 'inherit' | 'initial' | 'unset' | string
type TypeFontUnits = 'px' | 'rem' | 'em' | '%' | 'ch' | 'vw' | 'vh' | 'vmin' | 'vmax'
type TypeFontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 'lighter' | 'normal' | 'bold' | 'bolder'
type TypeFontStyle = 'normal' | 'italic' | 'oblique'
type TypeFontFamilyKey = keyof typeof getFontFamilyVarByKey

type TypeGetFont = {
  mobile?: boolean
  weight?: TypeFontWeight
  fontFamily?: TypeFontFamilyKey
  fontSize: TypeFontSize
  lineHeight?: TypeLineHeight
  fontSizeUnit?: TypeFontUnits
  lineHeightUnit?: TypeFontUnits
  fontStyle?: TypeFontStyle
  letterSpacing?: number
}

export const getFont = ({ mobile = false, weight = 400, letterSpacing, fontSize, fontSizeUnit = 'vw', lineHeight, lineHeightUnit = 'vw', fontStyle, fontFamily }:TypeGetFont) => css`
  font-size: ${typeof fontSize === 'number' ? fontSizeUnit === 'vw' ? vwFont(fontSize, mobile) : `${fontSize}${fontSizeUnit}` : fontSize};
  ${fontStyle !== undefined ? `font-style: ${fontStyle};` : ''}
  font-weight: ${weight};
  ${letterSpacing ? `letter-spacing: ${letterSpacing}px;` : ''}
  line-height: ${typeof lineHeight === 'number' ? lineHeightUnit === 'vw' ? vwFont(lineHeight, mobile) : `${lineHeight}${lineHeightUnit}` : lineHeight};
  ${fontFamily !== undefined ? `font-family: var(${getFontFamilyVarByKey[fontFamily]});` : ''}
`

type TypeFontProps = {
  fontWeight?: TypeFontWeight
  fontFamily?: TypeFontFamilyKey
  fontSize: TypeFontSize
  lineHeight?: TypeLineHeight
  fontSizeUnit?: TypeFontUnits
  lineHeightUnit?: TypeFontUnits
  fontStyle?: TypeFontStyle
  letterSpacing?: number
}

const breakpoints = {
  'watch': 180,
  'mobile-xs': 320,
  'mobile': 390,
  'nexus7': 603,
  'tablet': 768,
  'desktop-xs': 992,
  'desktop-md': 1200,
  'ipadpro-12': 1284,
  'desktop': 1440,
  'desktop-wide': 1650,
  'container': 1800,
  'desktop-large': 1920,
  'desktop-wqhd': 2560
} as const

type TypeBreakpointKey = keyof typeof breakpoints

const breakpointMinQueries = {
  mobile: '',
  tablet: `@media(min-width: ${breakpoints.tablet}px)`,
  desktop: `@media(min-width: ${breakpoints.desktop}px)`,
  'desktop-large': `@media(min-width: ${breakpoints['desktop-large']}px)`
} as const

type TypeBreakpointMinQueries = keyof typeof breakpointMinQueries
type TypeResponsiveFont = Partial<Record<TypeBreakpointMinQueries, TypeFontProps>>

export const getFontAdvanced = (fonts: TypeResponsiveFont) => {
  const styles = (Object.keys(fonts) as TypeBreakpointMinQueries[]).map(breakpoint => {
    const font = fonts[breakpoint]
    if (!font) return ''

    const {
      fontSize,
      lineHeight,
      fontSizeUnit = 'vw',
      lineHeightUnit = 'vw',
      fontWeight = 400,
      letterSpacing,
      fontStyle,
      fontFamily
    } = font

    const fontSizeValue = typeof fontSize === 'number'
      ? fontSizeUnit === 'vw'
        ? vwFont(fontSize, breakpoint === 'mobile')
        : `${fontSize}${fontSizeUnit}`
      : fontSize

    const lineHeightValue = typeof lineHeight === 'number'
      ? lineHeightUnit === 'vw'
        ? vwFont(lineHeight, breakpoint === 'mobile')
        : `${lineHeight}${lineHeightUnit}`
      : lineHeight

    const output = `
      font-size: ${fontSizeValue};
      font-weight: ${fontWeight};
      ${fontStyle !== undefined ? `font-style: ${fontStyle};` : ''}
      ${letterSpacing !== undefined && letterSpacing !== 0 ? `letter-spacing: ${letterSpacing}px;` : ''}
      ${lineHeight !== undefined ? `line-height: ${lineHeightValue};` : ''}
      ${fontFamily !== undefined ? `font-family: var(${getFontFamilyVarByKey[fontFamily]});` : ''}
    `

    if (breakpoint === 'mobile') return output

    return `
      ${breakpointMinQueries[breakpoint]} {
        ${output}
      }
    `
  })

  return css`${styles}`
}


type TypeFontFluidProps = {
  fontSizeMin: TypeFontSize
  fontSizeMax: TypeFontSize
  fontSizeUnit?: TypeFontUnits
  lineHeightMin?: TypeLineHeight
  lineHeightMax?: TypeLineHeight
  lineHeightUnit?: TypeFontUnits
  fontWeight?: TypeFontWeight
  fontFamily?: TypeFontFamilyKey
  fontStyle?: TypeFontStyle
  letterSpacingMin?: number
  letterSpacingMax?: number
  viewportMin?: number | TypeBreakpointKey
  viewportMax?: number | TypeBreakpointKey
}

// Helper to calculate clamp()
function generateFluidClamp(
  fontSizeMin: number,
  fontSizeMax: number,
  viewportMin: number,
  viewportMax: number,
  unit: TypeFontUnits
) {
  const slope = (fontSizeMax - fontSizeMin) / (viewportMax - viewportMin) * 100
  const yAxisIntersection = fontSizeMin - (slope * viewportMin) / 100

  return `clamp(${fontSizeMin}${unit}, ${yAxisIntersection.toFixed(4)}${unit} + ${slope.toFixed(4)}vw, ${fontSizeMax}${unit})`
}

// Resolve a breakpoint value
function resolveBreakpoint(value: number | TypeBreakpointKey): number {
  if (typeof value === 'number') return value
  return breakpoints[value]
}

// Updated getFontFluid
export const getFontFluid = ({
  fontSizeMin,
  fontSizeMax,
  fontSizeUnit = 'px',
  lineHeightMin,
  lineHeightMax,
  lineHeightUnit = 'px',
  fontWeight = 400,
  fontFamily,
  fontStyle,
  letterSpacingMin,
  letterSpacingMax,
  viewportMin = 'mobile',
  viewportMax = 'desktop'
}: TypeFontFluidProps) => css`
  font-size: ${typeof fontSizeMin === 'number' && typeof fontSizeMax === 'number'
    ? generateFluidClamp(
      fontSizeMin,
      fontSizeMax,
      resolveBreakpoint(viewportMin),
      resolveBreakpoint(viewportMax),
      fontSizeUnit
    )
    : fontSizeMin};

  font-weight: ${fontWeight};
  ${fontStyle ? `font-style: ${fontStyle};` : ''}
  ${fontFamily ? `font-family: var(${getFontFamilyVarByKey[fontFamily]});` : ''}

  ${lineHeightMin !== undefined && lineHeightMax !== undefined ? `
    line-height: ${typeof lineHeightMin === 'number' && typeof lineHeightMax === 'number' ?
    generateFluidClamp(
      lineHeightMin,
      lineHeightMax,
      resolveBreakpoint(viewportMin),
      resolveBreakpoint(viewportMax),
      lineHeightUnit
    )
    : lineHeightMin};
  ` : ''}

  ${letterSpacingMin !== undefined && letterSpacingMax !== undefined ? `letter-spacing: ${
    generateFluidClamp(
      letterSpacingMin,
      letterSpacingMax,
      resolveBreakpoint(viewportMin),
      resolveBreakpoint(viewportMax),
      fontSizeUnit
    )};
  ` : ''}
`

// const font = {
//   getP141
//   getP113
//   getP93
//   getP93_1
//   getP53
//   getP50_1
//   getP53_2
//   getP45

//   getP35
//   getP30
//   getP30_1
//   getP30_2

//   getP26
//   getP26_1
//   getP26_2
//   getP26_3
//   getP26_4
//   getP25

//   getP20
//   getP20_1
//   getP20_5
//   getP20_2
//   getP20_3
//   getP20_4
//   getP20_Mobile

//   getP18_1
//   getP18_2
//   getP18_3
//   getP18_4
//   getP18InputLabel
//   getP18
//   getP18InputHasContentLabel
//   getP18_1B

//   getP16

//   getP15_1
//   getP15
//   getP15_1B
//   getP15_2B
//   getP15_2
//   getP15_3
//   getP15_3B
//   getP15_4
//   getP15_5
//   getP15_6

//   getP13_3

//   getP12
//   getP12_1
//   getP12_1B
//   getP12_2
//   getP12_3
//   getP10_1
//   getP10

//   getP9
//   getP14Search
//   getP15Search
//   getP27_P18
//   getP20v2
//   getP17NavLinks
//   font12_15
// }

export const presets = {
  font141: () => getFontAdvanced({
    mobile: {
      fontSize: 75,
      fontWeight: 700,
      lineHeight: 64,
      letterSpacing: 1.56
    },
    tablet: {
      fontSize: 141,
      fontWeight: 700,
      lineHeight: 122,
      letterSpacing: 2.94
    }
  }),

  font113: () => getFontAdvanced({
    mobile: {
      fontSize: 55,
      fontWeight: 700,
      lineHeight: 50,
      letterSpacing: .53
    },
    tablet: {
      fontSize: 113,
      fontWeight: 700,
      lineHeight: 97,
      letterSpacing: 1
    }
  }),

  font93: () => getFontAdvanced({
    mobile: {
      fontSize: 45,
      fontWeight: 'bold',
      lineHeight: 54,
      letterSpacing: 1.45
    },
    tablet: {
      fontSize: 93,
      fontWeight: 'bold',
      lineHeight: 112,
      letterSpacing: 3
    }
  }),

  font93_1: () => getFontAdvanced({
    mobile: {
      fontSize: 45,
      fontWeight: 700,
      lineHeight: 40,
      letterSpacing: 1.45
    },
    tablet: {
      fontSize: 93,
      lineHeight: 83,
      letterSpacing: 1,
      fontWeight: 700
    }
  }),

  font53: () => getFontAdvanced({
    mobile: {
      fontSize: 27,
      fontWeight: 'bold',
      lineHeight: 29,
      letterSpacing: .56
    },
    tablet: {
      fontSize: 53,
      fontWeight: 'bold',
      lineHeight: 57,
      letterSpacing: 1
    }
  }),

  font50_1: () => getFontAdvanced({
    mobile: {
      fontSize: 50,
      fontWeight: 'normal',
      lineHeight: 54,
      letterSpacing: -3.81,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 50,
      fontWeight: 'normal',
      lineHeight: 54,
      letterSpacing: -3.81,
    }
  }),

  font53_2: () => getFontAdvanced({
    mobile: {
      fontSize: 27,
      fontWeight: 700,
      lineHeight: 29,
      letterSpacing: .56,
      fontFamily: 'platform'
    },
    tablet: {
      fontSize: 53,
      fontWeight: 700,
      fontFamily: 'platform',
      lineHeight: 45,
      letterSpacing: .59,
    }
  }),

  font45: () => getFontAdvanced({
    mobile: {
      fontSize: 36,
      fontWeight: 700,
      lineHeight: 40,
      letterSpacing: .37
    },
    tablet: {
      fontSize: 45,
      fontWeight: 700,
      lineHeight: 40,
      letterSpacing: .56
    }
  }),

  font35: () => getFontAdvanced({
    mobile: {
      fontSize: 28,
      fontWeight: 400,
      lineHeight: 32,
      letterSpacing: -1.13,
      fontFamily: 'graphik' // secondaryFont → graphik
    },
    tablet: {
      fontSize: 35,
      fontWeight: 400,
      lineHeight: 38.8,
      letterSpacing: -1.67,
      fontFamily: 'graphik' // secondaryFont → graphik
    }
  }),

  font30: () => getFontAdvanced({
    mobile: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 24,
      letterSpacing: -.2
    },
    tablet: {
      fontSize: 30,
      fontWeight: 700,
      lineHeight: 42,
      letterSpacing: -.3
    }
  }),

  font30_1: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 300,
      lineHeight: 19,
      letterSpacing: -.8,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 30,
      fontWeight: 300,
      lineHeight: 37,
      letterSpacing: -1.5,
      fontFamily: 'graphik'
    }
  }),

  font30_2: () => getFontAdvanced({
    mobile: {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 35,
      letterSpacing: .27,
      fontFamily: 'platform'
    },
    tablet: {
      fontSize: 30,
      fontWeight: 700,
      lineHeight: 42,
      letterSpacing: .33,
      fontFamily: 'platform'
    }
  }),

  font26: () => getFontAdvanced({
    mobile: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 24,
      letterSpacing: -.22,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 26,
      fontWeight: 700,
      lineHeight: 29,
      letterSpacing: -.26,
      fontFamily: 'basier'
    }
  }),

  font26_1: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 'normal',
      lineHeight: 19,
      letterSpacing: -.8,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 26,
      fontWeight: 300,
      lineHeight: 29,
      letterSpacing: -.3,
      fontFamily: 'graphik'
    }
  }),

  font26_2: () => getFontAdvanced({
    mobile: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 24,
      letterSpacing: .22
    },
    tablet: {
      fontSize: 26,
      fontWeight: 700,
      lineHeight: 30,
      letterSpacing: -.26
    }
  }),

  font26_3: () => getFontAdvanced({
    mobile: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 21,
      letterSpacing: .22,
      fontFamily: 'platform'
    },
    tablet: {
      fontSize: 26,
      fontWeight: 700,
      lineHeight: 21,
      letterSpacing: .29,
      fontFamily: 'platform'
    }
  }),

  font26_4: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 300,
      lineHeight: 20,
      letterSpacing: -.8,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 26,
      fontWeight: 300,
      lineHeight: 34,
      letterSpacing: -1.41,
      fontFamily: 'graphik'
    }
  }),

  font26_5: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: 20,
      letterSpacing: -0.5,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 26,
      fontWeight: 500,
      lineHeight: 34,
      letterSpacing: -0.5,
      fontFamily: 'graphik'
    }
  }),

  font25: () => getFontAdvanced({
    mobile: {
      fontSize: 19,
      fontWeight: 700,
      lineHeight: 24,
      letterSpacing: -.22,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 25,
      fontWeight: 700,
      lineHeight: 29,
      letterSpacing: -.26,
      fontFamily: 'basier'
    }
  }),

  font20: () => getFontAdvanced({
    mobile: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 24,
      letterSpacing: .5
    },
    tablet: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 24,
    }
  }),

  font20_1: () => getFontAdvanced({
    mobile: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 15,
      letterSpacing: -.36,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 20,
      fontWeight: 500,
      lineHeight: 26,
      letterSpacing: -.5,
      fontFamily: 'basier'
    }
  }),

  font20_5: () => getFontAdvanced({
    mobile: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 15,
      letterSpacing: -.36,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 20,
      fontWeight: 500,
      lineHeight: 26,
      letterSpacing: -.5,
      fontFamily: 'graphik'
    }
  }),

  font20_2: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: 19,
      letterSpacing: -.8,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 20,
      fontWeight: 500,
      lineHeight: 26,
      letterSpacing: -.5,
      fontFamily: 'graphik'
    }
  }),

  font20_3: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 'bold',
      lineHeight: 19,
      letterSpacing: -.8
    },
    tablet: {
      fontSize: 20,
      fontWeight: 'bold',
      lineHeight: 16,
      letterSpacing: -.5
    }
  }),

  font20_4: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 300,
      lineHeight: 19,
      letterSpacing: -.8,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 20,
      fontWeight: 300,
      lineHeight: 26,
      letterSpacing: -.5,
      fontFamily: 'graphik'
    }
  }),

  font20_Mobile: () => getFontAdvanced({
    mobile: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 25,
      letterSpacing: -.23
    }
  }),

  font18_1: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 300,
      lineHeight: 19,
      letterSpacing: -.3,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 18,
      fontWeight: 300,
      lineHeight: 22,
      letterSpacing: -.36,
      fontFamily: 'graphik'
    }
  }),

  font18_2: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 19,
      letterSpacing: -.3,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 25,
      letterSpacing: -.36,
      fontFamily: 'basier'
    }
  }),


  font18_3: () => getFontAdvanced({
    mobile: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 22,
      letterSpacing: -.36,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 22,
      fontFamily: 'basier'
    }
  }),

  font18_4: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 700,
      lineHeight: 19,
      letterSpacing: -.3,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 22,
      letterSpacing: -.36,
      fontFamily: 'graphik'
    }
  }),

  font18InputLabel: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 15,
      letterSpacing: -.3,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 15,
      letterSpacing: -.36,
      fontFamily: 'basier'
    }
  }),

  font18: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 15,
      letterSpacing: -.3,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 15,
      letterSpacing: -.36,
      fontFamily: 'graphik'
    }
  }),

  font18InputHasContentLabel: () => getFontAdvanced({
    mobile: {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 10,
      letterSpacing: -.3,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 10,
      letterSpacing: -.36,
      fontFamily: 'graphik'
    }
  }),

  font18_1B: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 19,
      letterSpacing: -.3,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 18,
      fontWeight: 700,
      lineHeight: 22,
      letterSpacing: 0,
      fontFamily: 'graphik'
    }
  }),

  font16: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: 19,
      letterSpacing: -.8,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: 19,
      letterSpacing: 0,
      fontFamily: 'basier'
    }
  }),
  font16whatsapp: () => getFontAdvanced({
    mobile: {
      fontSize: 16,
      fontWeight: 700,
      lineHeight: 17.6,
      letterSpacing: .538,
      fontFamily: 'platform'
    },
    tablet: {
      fontSize: 20,
      fontWeight: 700,
      lineHeight: 22,
      letterSpacing: .538,
      fontFamily: 'platform'
    }
  }),
  font15_1: () => getFontAdvanced({
    mobile: {
      fontSize: 12,
      fontWeight: 300,
      lineHeight: 15,
      letterSpacing: -.4,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 15,
      lineHeight: 20,
      letterSpacing: -.5
    }
  }),

  font15: () => getFontAdvanced({
    mobile: {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 15,
      letterSpacing: .13
    },
    tablet: {
      fontSize: 15,
      lineHeight: 21,
      letterSpacing: .17
    }
  }),

  font15_1B: () => getFontAdvanced({
    mobile: {
      fontSize: 13,
      fontWeight: 300,
      lineHeight: 15,
      letterSpacing: -.4,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 15,
      lineHeight: 20,
      letterSpacing: -.5
    }
  }),

  font15_2B: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 15,
      letterSpacing: -.3,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 15,
      lineHeight: 15,
      letterSpacing: -.36
    }
  }),

  font15_2: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 19,
      letterSpacing: -.38,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 15,
      lineHeight: 19,
    }
  }),

  font15_3: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 700,
      lineHeight: 25,
      letterSpacing: -.38,
      fontFamily: 'platform'
    },
    tablet: {
      fontSize: 15,
      lineHeight: 21,
      letterSpacing: .17
    }
  }),

  font15_3B: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 700,
      lineHeight: 20,
      letterSpacing: 0,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 15,
      lineHeight: 20,
    }
  }),

  font15_4: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 18,
      letterSpacing: -.38
    },
    tablet: {
      fontSize: 15,
      lineHeight: 18,
    }
  }),

  font15_5: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 300,
      lineHeight: 14,
      letterSpacing: -.38,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 15,
      lineHeight: 14,
    }
  }),

  font15_6: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 14,
      letterSpacing: -.38,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 15,
      lineHeight: 14,
    }
  }),
  font14whatsapp: () => getFontAdvanced({
    mobile: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 22,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 22,
      fontFamily: 'graphik'
    }
  }),
  font13: () => getFontAdvanced({
    mobile: {
      fontSize: 9,
      fontWeight: 700,
      lineHeight: 15,
      letterSpacing: -.38,
      fontFamily: 'platform'
    },
    tablet: {
      fontSize: 13,
      lineHeight: 21,
      letterSpacing: .17
    }
  }),
  font12: () => getFontAdvanced({
    mobile: {
      fontSize: 9,
      fontWeight: 700,
      lineHeight: 15,
      letterSpacing: .1
    },
    tablet: {
      fontSize: 12,
      lineHeight: 21,
      letterSpacing: .13
    }
  }),

  font12_1: () => getFontAdvanced({
    mobile: {
      fontSize: 10,
      fontWeight: 300,
      lineHeight: 13,
      letterSpacing: -.33,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 12,
      lineHeight: 14,
      letterSpacing: -.3
    }
  }),

  font12_1B: () => getFontAdvanced({
    mobile: {
      fontSize: 10,
      fontWeight: 300,
      lineHeight: 13,
      letterSpacing: -.33,
      fontFamily: 'basier'
    },
    tablet: {
      fontSize: 12,
      lineHeight: 14,
      letterSpacing: -.3
    }
  }),

  font12_2: () => getFontAdvanced({
    mobile: {
      fontSize: 10,
      fontWeight: 300,
      lineHeight: 13,
      letterSpacing: -.33,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 12,
      lineHeight: 14,
      letterSpacing: -.3
    }
  }),

  font12_3: () => getFontAdvanced({
    mobile: {
      fontSize: 10,
      fontWeight: 'normal',
      lineHeight: 25,
      letterSpacing: -.5,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 12,
      lineHeight: 20,
      letterSpacing: -.5
    }
  }),

  font10_1: () => getFontAdvanced({
    mobile: {
      fontSize: 8.5,
      fontWeight: 300,
      lineHeight: 15,
      letterSpacing: 0,
      fontFamily: 'graphik'
    },
    tablet: {
      fontSize: 10,
      lineHeight: 15
    }
  }),

  font10: () => getFontAdvanced({
    mobile: {
      fontSize: 11,
      fontWeight: 700,
      lineHeight: 15,
      letterSpacing: .25
    },
    tablet: {
      fontSize: 10,
      lineHeight: 15,
      letterSpacing: .25
    }
  }),

  font9: () => getFontAdvanced({
    mobile: {
      fontSize: 10,
      fontWeight: 300,
      lineHeight: 12,
      letterSpacing: .25
    },
    tablet: {
      fontSize: 10,
      lineHeight: 12,
      letterSpacing: .25
    }
  }),

  font14Search: () => getFontAdvanced({
    tablet: {
      fontSize: 14,
      lineHeight: 17
    }
  }),

  font15Search: () => getFontAdvanced({
    mobile: {
      fontSize: 15,
      fontWeight: 500,
      lineHeight: 18,
      letterSpacing: -.38
    },
    tablet: {
      fontSize: 15,
      lineHeight: 18
    }
  }),

  font27_P18: () => getFontAdvanced({
    mobile: {
      fontSize: 27,
      fontWeight: 700,
      lineHeight: 29,
      letterSpacing: .56
    },
    tablet: {
      fontSize: 18,
      lineHeight: 22,
      letterSpacing: -.36
    }
  }),

  font20v2: () => getFontAdvanced({
    mobile: {
      fontSize: 20,
      lineHeight: 24,
      letterSpacing: .5
    },
    tablet: {
      fontSize: 20,
      lineHeight: 24,
      letterSpacing: .5
    }
  }),

  font17NavLinks: () => getFontAdvanced({
    mobile: {
      fontSize: '17px',
      fontWeight: 'bold',
      letterSpacing: 0,
      fontFamily: 'platform'
    },
    tablet: {
      fontSize: 17,
      lineHeight: 18
    }
  })
}


export const presetsFluid = {
  font141: () => getFontFluid({
    fontSizeMin: 75,
    fontSizeMax: 141,
    fontWeight: 700,
    lineHeightMin: 64,
    lineHeightMax: 122,
    letterSpacingMin: 1.56,
    letterSpacingMax: 2.94
  }),

  font113: () => getFontFluid({
    fontSizeMin: 55,
    fontSizeMax: 113,
    fontWeight: 700,
    lineHeightMin: 50,
    lineHeightMax: 97,
    letterSpacingMin: .53,
    letterSpacingMax: 1
  }),

  font93: () => getFontFluid({
    fontSizeMin: 45,
    fontSizeMax: 93,
    fontWeight: 'bold',
    lineHeightMin: 54,
    lineHeightMax: 112,
    letterSpacingMin: 1.45,
    letterSpacingMax: 3
  }),

  font93_1: () => getFontFluid({
    fontSizeMin: 45,
    fontSizeMax: 93,
    fontWeight: 700,
    lineHeightMin: 40,
    lineHeightMax: 83,
    letterSpacingMin: 1.45,
    letterSpacingMax: 1
  }),

  font53: () => getFontFluid({
    fontSizeMin: 27,
    fontSizeMax: 53,
    fontWeight: 'bold',
    lineHeightMin: 29,
    lineHeightMax: 57,
    letterSpacingMin: .56,
    letterSpacingMax: 1
  }),

  font50_1: () => getFontFluid({
    fontSizeMin: 50,
    fontSizeMax: 50,
    fontWeight: 'normal',
    lineHeightMin: 54,
    lineHeightMax: 54,
    letterSpacingMin: -3.81,
    letterSpacingMax: -3.81,
    fontFamily: 'basier'
  }),

  font53_2: () => getFontFluid({
    fontSizeMin: 27,
    fontSizeMax: 53,
    fontWeight: 700,
    lineHeightMin: 29,
    lineHeightMax: 45,
    letterSpacingMin: .56,
    letterSpacingMax: .59,
    fontFamily: 'platform'
  }),

  font45: () => getFontFluid({
    fontSizeMin: 36,
    fontSizeMax: 45,
    fontWeight: 700,
    lineHeightMin: 40,
    lineHeightMax: 48,
    letterSpacingMin: .37,
    letterSpacingMax: .56
  }),

  font35: () => getFontFluid({
    fontSizeMin: 28,
    fontSizeMax: 35,
    fontWeight: 400,
    lineHeightMin: 32,
    lineHeightMax: 38.8,
    letterSpacingMin: -1.13,
    letterSpacingMax: -1.67,
    fontFamily: 'graphik'
  }),

  font30: () => getFontFluid({
    fontSizeMin: 20,
    fontSizeMax: 30,
    fontWeight: 700,
    lineHeightMin: 24,
    lineHeightMax: 42,
    letterSpacingMin: -.2,
    letterSpacingMax: -.3
  }),

  font30_1: () => getFontFluid({
    fontSizeMin: 16,
    fontSizeMax: 30,
    fontWeight: 300,
    lineHeightMin: 19,
    lineHeightMax: 37,
    letterSpacingMin: -.8,
    letterSpacingMax: -1.5,
    fontFamily: 'graphik'
  }),

  font30_2: () => getFontFluid({
    fontSizeMin: 24,
    fontSizeMax: 30,
    fontWeight: 700,
    lineHeightMin: 35,
    lineHeightMax: 42,
    letterSpacingMin: .27,
    letterSpacingMax: .33,
    fontFamily: 'platform'
  }),

  font26: () => getFontFluid({
    fontSizeMin: 20,
    fontSizeMax: 26,
    fontWeight: 700,
    lineHeightMin: 24,
    lineHeightMax: 29,
    letterSpacingMin: -.22,
    letterSpacingMax: -.26,
    fontFamily: 'basier'
  }),

  font26_1: () => getFontFluid({
    fontSizeMin: 16,
    fontSizeMax: 26,
    fontWeight: 'normal',
    lineHeightMin: 19,
    lineHeightMax: 29,
    letterSpacingMin: -.8,
    letterSpacingMax: -.3,
    fontFamily: 'graphik'
  }),

  font26_2: () => getFontFluid({
    fontSizeMin: 20,
    fontSizeMax: 26,
    fontWeight: 700,
    lineHeightMin: 24,
    lineHeightMax: 30,
    letterSpacingMin: .22,
    letterSpacingMax: -.26
  }),

  font26_3: () => getFontFluid({
    fontSizeMin: 20,
    fontSizeMax: 26,
    fontWeight: 700,
    lineHeightMin: 21,
    lineHeightMax: 21,
    letterSpacingMin: .22,
    letterSpacingMax: .29,
    fontFamily: 'platform'
  }),

  font26_4: () => getFontFluid({
    fontSizeMin: 16,
    fontSizeMax: 26,
    fontWeight: 300,
    lineHeightMin: 20,
    lineHeightMax: 34,
    letterSpacingMin: -.8,
    letterSpacingMax: -1.41,
    fontFamily: 'graphik'
  }),

  font25: () => getFontFluid({
    fontSizeMin: 19,
    fontSizeMax: 25,
    fontWeight: 700,
    lineHeightMin: 24,
    lineHeightMax: 29,
    letterSpacingMin: -.22,
    letterSpacingMax: -.26,
    fontFamily: 'basier'
  }),

  font20: () => getFontFluid({
    fontSizeMin: 20,
    fontSizeMax: 20,
    fontWeight: 700,
    lineHeightMin: 24,
    lineHeightMax: 24,
    letterSpacingMin: .5,
    letterSpacingMax: .5
  }),

  font20_1: () => getFontFluid({
    fontSizeMin: 18,
    fontSizeMax: 20,
    fontWeight: 500,
    lineHeightMin: 15,
    lineHeightMax: 26,
    letterSpacingMin: -.36,
    letterSpacingMax: -.5,
    fontFamily: 'basier'
  }),

  font20_5: () => getFontFluid({
    fontSizeMin: 18,
    fontSizeMax: 20,
    fontWeight: 500,
    lineHeightMin: 15,
    lineHeightMax: 26,
    letterSpacingMin: -.36,
    letterSpacingMax: -.5,
    fontFamily: 'graphik'
  }),

  font20_2: () => getFontFluid({
    fontSizeMin: 16,
    fontSizeMax: 20,
    fontWeight: 500,
    lineHeightMin: 19,
    lineHeightMax: 26,
    letterSpacingMin: -.8,
    letterSpacingMax: -.5,
    fontFamily: 'graphik'
  }),

  font20_3: () => getFontFluid({
    fontSizeMin: 16,
    fontSizeMax: 20,
    fontWeight: 'bold',
    lineHeightMin: 19,
    lineHeightMax: 16,
    letterSpacingMin: -.8,
    letterSpacingMax: -.5
  }),

  font20_4: () => getFontFluid({
    fontSizeMin: 16,
    fontSizeMax: 20,
    fontWeight: 300,
    lineHeightMin: 19,
    lineHeightMax: 26,
    letterSpacingMin: -.8,
    letterSpacingMax: -.5,
    fontFamily: 'graphik'
  }),

  font20_Mobile: () => getFontFluid({
    fontSizeMin: 20,
    fontSizeMax: 20,
    fontWeight: 600,
    lineHeightMin: 25,
    lineHeightMax: 25,
    letterSpacingMin: -.23,
    letterSpacingMax: -.23
  }),
  font18_1: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 18,
    fontWeight: 300,
    lineHeightMin: 19,
    lineHeightMax: 22,
    letterSpacingMin: -.3,
    letterSpacingMax: -.36,
    fontFamily: 'graphik'
  }),

  font18_2: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 18,
    fontWeight: 500,
    lineHeightMin: 19,
    lineHeightMax: 25,
    letterSpacingMin: -.3,
    letterSpacingMax: -.36,
    fontFamily: 'basier'
  }),

  font18_3: () => getFontFluid({
    fontSizeMin: 18,
    fontSizeMax: 18,
    fontWeight: 500,
    lineHeightMin: 22,
    lineHeightMax: 22,
    letterSpacingMin: -.36,
    letterSpacingMax: -.36,
    fontFamily: 'basier'
  }),

  font18_4: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 18,
    fontWeight: 700,
    lineHeightMin: 19,
    lineHeightMax: 22,
    letterSpacingMin: -.3,
    letterSpacingMax: -.36,
    fontFamily: 'graphik'
  }),

  font18InputLabel: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 18,
    fontWeight: 500,
    lineHeightMin: 15,
    lineHeightMax: 15,
    letterSpacingMin: -.3,
    letterSpacingMax: -.36,
    fontFamily: 'basier'
  }),

  font18: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 18,
    fontWeight: 500,
    lineHeightMin: 15,
    lineHeightMax: 15,
    letterSpacingMin: -.3,
    letterSpacingMax: -.36,
    fontFamily: 'graphik'
  }),

  font18InputHasContentLabel: () => getFontFluid({
    fontSizeMin: 12,
    fontSizeMax: 12,
    fontWeight: 500,
    lineHeightMin: 10,
    lineHeightMax: 10,
    letterSpacingMin: -.3,
    letterSpacingMax: -.36,
    fontFamily: 'graphik'
  }),

  font18_1B: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 18,
    fontWeight: 500,
    lineHeightMin: 19,
    lineHeightMax: 22,
    letterSpacingMin: -.3,
    letterSpacingMax: 0,
    fontFamily: 'graphik'
  }),

  font16: () => getFontFluid({
    fontSizeMin: 16,
    fontSizeMax: 16,
    fontWeight: 500,
    lineHeightMin: 19,
    lineHeightMax: 19,
    letterSpacingMin: -.8,
    letterSpacingMax: 0,
    fontFamily: 'basier'
  }),

  font15_1: () => getFontFluid({
    fontSizeMin: 12,
    fontSizeMax: 15,
    fontWeight: 300,
    lineHeightMin: 15,
    lineHeightMax: 20,
    letterSpacingMin: -.4,
    letterSpacingMax: -.5,
    fontFamily: 'graphik'
  }),

  font15: () => getFontFluid({
    fontSizeMin: 12,
    fontSizeMax: 15,
    fontWeight: 500,
    lineHeightMin: 15,
    lineHeightMax: 21,
    letterSpacingMin: .13,
    letterSpacingMax: .17
  }),

  font15_1B: () => getFontFluid({
    fontSizeMin: 13,
    fontSizeMax: 15,
    fontWeight: 300,
    lineHeightMin: 15,
    lineHeightMax: 20,
    letterSpacingMin: -.4,
    letterSpacingMax: -.5,
    fontFamily: 'graphik'
  }),

  font15_2B: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 15,
    fontWeight: 500,
    lineHeightMin: 15,
    lineHeightMax: 15,
    letterSpacingMin: -.3,
    letterSpacingMax: -.36,
    fontFamily: 'graphik'
  }),

  font15_2: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 15,
    fontWeight: 500,
    lineHeightMin: 19,
    lineHeightMax: 19,
    letterSpacingMin: -.38,
    letterSpacingMax: -.38,
    fontFamily: 'basier'
  }),

  font15_3: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 15,
    fontWeight: 700,
    lineHeightMin: 25,
    lineHeightMax: 21,
    letterSpacingMin: -.38,
    letterSpacingMax: .17,
    fontFamily: 'platform'
  }),

  font15_3B: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 15,
    fontWeight: 700,
    lineHeightMin: 20,
    lineHeightMax: 20,
    letterSpacingMin: 0,
    letterSpacingMax: 0,
    fontFamily: 'graphik'
  }),

  font15_4: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 15,
    fontWeight: 500,
    lineHeightMin: 18,
    lineHeightMax: 18,
    letterSpacingMin: -.38,
    letterSpacingMax: -.38
  }),

  font15_5: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 15,
    fontWeight: 300,
    lineHeightMin: 14,
    lineHeightMax: 14,
    letterSpacingMin: -.38,
    letterSpacingMax: -.38,
    fontFamily: 'graphik'
  }),

  font15_6: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 15,
    fontWeight: 500,
    lineHeightMin: 14,
    lineHeightMax: 14,
    letterSpacingMin: -.38,
    letterSpacingMax: -.38,
    fontFamily: 'graphik'
  }),

  font13: () => getFontFluid({
    fontSizeMin: 9,
    fontSizeMax: 13,
    fontWeight: 700,
    lineHeightMin: 15,
    lineHeightMax: 21,
    letterSpacingMin: -.38,
    letterSpacingMax: .17,
    fontFamily: 'platform'
  }),

  font12: () => getFontFluid({
    fontSizeMin: 9,
    fontSizeMax: 12,
    fontWeight: 700,
    lineHeightMin: 15,
    lineHeightMax: 21,
    letterSpacingMin: .1,
    letterSpacingMax: .13
  }),

  font12_1: () => getFontFluid({
    fontSizeMin: 10,
    fontSizeMax: 12,
    fontWeight: 300,
    lineHeightMin: 13,
    lineHeightMax: 14,
    letterSpacingMin: -.33,
    letterSpacingMax: -.3,
    fontFamily: 'graphik'
  }),

  font12_1B: () => getFontFluid({
    fontSizeMin: 10,
    fontSizeMax: 12,
    fontWeight: 300,
    lineHeightMin: 13,
    lineHeightMax: 14,
    letterSpacingMin: -.33,
    letterSpacingMax: -.3,
    fontFamily: 'basier'
  }),

  font12_2: () => getFontFluid({
    fontSizeMin: 10,
    fontSizeMax: 12,
    fontWeight: 300,
    lineHeightMin: 13,
    lineHeightMax: 14,
    letterSpacingMin: -.33,
    letterSpacingMax: -.3,
    fontFamily: 'graphik'
  }),

  font12_3: () => getFontFluid({
    fontSizeMin: 10,
    fontSizeMax: 12,
    fontWeight: 'normal',
    lineHeightMin: 25,
    lineHeightMax: 20,
    letterSpacingMin: -.5,
    letterSpacingMax: -.5,
    fontFamily: 'graphik'
  }),

  font10_1: () => getFontFluid({
    fontSizeMin: 8.5,
    fontSizeMax: 10,
    fontWeight: 300,
    lineHeightMin: 15,
    lineHeightMax: 15,
    letterSpacingMin: 0,
    letterSpacingMax: 0,
    fontFamily: 'graphik'
  }),

  font10: () => getFontFluid({
    fontSizeMin: 10,
    fontSizeMax: 10,
    fontWeight: 700,
    lineHeightMin: 15,
    lineHeightMax: 15,
    letterSpacingMin: .25,
    letterSpacingMax: .25
  }),

  font9: () => getFontFluid({
    fontSizeMin: 10,
    fontSizeMax: 10,
    fontWeight: 300,
    lineHeightMin: 12,
    lineHeightMax: 12,
    letterSpacingMin: .25,
    letterSpacingMax: .25
  }),

  font14Search: () => getFontFluid({
    fontSizeMin: 14,
    fontSizeMax: 14,
    lineHeightMin: 17,
    lineHeightMax: 17
  }),

  font15Search: () => getFontFluid({
    fontSizeMin: 15,
    fontSizeMax: 15,
    fontWeight: 500,
    lineHeightMin: 18,
    lineHeightMax: 18,
    letterSpacingMin: -.38,
    letterSpacingMax: -.38
  }),

  font27_P18: () => getFontFluid({
    fontSizeMin: 27,
    fontSizeMax: 18,
    fontWeight: 700,
    lineHeightMin: 29,
    lineHeightMax: 22,
    letterSpacingMin: .56,
    letterSpacingMax: -.36
  }),

  font20v2: () => getFontFluid({
    fontSizeMin: 20,
    fontSizeMax: 20,
    lineHeightMin: 24,
    lineHeightMax: 24,
    letterSpacingMin: .5,
    letterSpacingMax: .5
  }),

  font17NavLinks: () => getFontFluid({
    fontSizeMin: 17,
    fontSizeMax: 17,
    fontWeight: 'bold',
    lineHeightMin: 18,
    lineHeightMax: 18,
    letterSpacingMin: 0,
    letterSpacingMax: 0,
    fontFamily: 'platform'
  }),

}



